import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { SQL } from '@/lib/Sqlite'
import { ChangedItem, DeviceEventLog, LogItem } from '@/app/data/type'
import { getRandomString, getUnixtime } from '@/lib/Functions'
import { prepareSQLiteValue } from '@/lib/Sqlite/schema'
import { getDynamicTableSql, getDeviceOrClientId, parseRow } from '@/app/util'
import { APIServer } from '@/app/data/api'
import { Network } from '@capacitor/network'
import _ from 'lodash'
import { isApp } from '@/index'
import { App } from '..'
import { syncQueue, JobPriority } from '@/app/sync'
import { DependencyNotReadyError } from '@/app/sync/syncError'
import Merge from '../../lib/Task/merge'

// ─────────────────────────────────────────────────────────────────────────────
// device_event_log(변경 이력 로그) 기능 전역 비활성화 플래그 (2026-06-10)
//
// 비활성화 사유:
// - 현재 실용성이 없는 기능임에도 변경 필드당 1건씩 로그가 무한 누적되고,
//   미동기화 로그가 수만 건 쌓이면 매 sync 주기의 전체 SELECT가 Capacitor 브리지에서
//   거대 JSON 직렬화(실측 150MB)를 일으켜 OutOfMemoryError 크래시를 유발함.
//   (실기기 재현: logs/perf-20260610-145959.log — Bridge.callPluginMethod OOM FATAL)
//
// 재활성화 방법: 아래 값을 true로 변경.
// - 테이블 스키마와 기존 데이터는 그대로 유지되어 있어 코드 외 추가 작업 없음.
// - 단, 재활성화 전 "미동기화 로그 누적 정리 정책"(영구 실패 로그 처리)을 먼저 마련할 것.
// ─────────────────────────────────────────────────────────────────────────────
export const DEVICE_EVENT_LOG_ENABLED: boolean = false
// import { useAppStateStore } from './state' // 사용하지 않음
// 서버 응답 로그 타입 정의 추가 (company 필드 포함)
interface ServerEventLog extends DeviceEventLog {
  company?: string
}

const storeKey = 'device_event_log'
const ADD_LOG_KEYS = [
  'task_id',
  'event_type',
  'environment',
  'author',
  'old_value',
  'new_value',
  'at',
]

const EXCLUDE_API_UPDATE_KEYS = [
  'shapes.hasPhoto.update',
  'shapes.floorplan_id.update',
  'shapes.mainPhotoUrl.update',
  'shapes.indexing.update',
  'shapes.hasPhoto.create',
  'shapes.mainPhotoUrl.create',
  'shapes.indexing.create',
  'shapes.floorplan_id.create',
]

// JSON 문자열 값을 안전하게 처리하는 함수 수정
const parseJsonStringValue = (value: string | null): unknown => {
  if (value === null || value === undefined) return null

  try {
    // JSON 문자열 파싱 시도
    const parsed = JSON.parse(value)

    // 만약 파싱된 결과가 다시 문자열이고 JSON 형태라면 한번 더 파싱
    if (
      typeof parsed === 'string' &&
      (parsed.startsWith('{') ||
        parsed.startsWith('[') ||
        parsed.startsWith('"') ||
        parsed === 'null' ||
        parsed === 'true' ||
        parsed === 'false')
    ) {
      try {
        return JSON.parse(parsed)
      } catch (_e) {
        // 두 번째 파싱 실패 시 원래 파싱 결과 반환
        return parsed
      }
    }

    return parsed
  } catch (_e) {
    // 파싱 실패 시 원래 값 반환
    return value
  }
}

// 서버로 전송 전 이벤트 로그 데이터 정제 함수
const sanitizeEventLogForServer = (log: DeviceEventLog): DeviceEventLog => {
  // 빈 값은 null로 통일
  const sanitized = { ...log }

  try {
    if (sanitized.old_value === undefined || sanitized.old_value === '') {
      sanitized.old_value = 'null'
    } else if (typeof sanitized.old_value === 'function') {
      sanitized.old_value = 'null' // 함수는 로그에 저장하지 않음
    } else if (typeof sanitized.old_value === 'object') {
      sanitized.old_value = JSON.stringify(sanitized.old_value)
    }

    if (sanitized.new_value === undefined || sanitized.new_value === '') {
      sanitized.new_value = 'null'
    } else if (typeof sanitized.new_value === 'function') {
      sanitized.new_value = 'null' // 함수는 로그에 저장하지 않음
    } else if (typeof sanitized.new_value === 'object') {
      sanitized.new_value = JSON.stringify(sanitized.new_value)
    }
  } catch (_e) {
    console.error('Error converting values to string:', _e)
    sanitized.old_value = 'null'
    sanitized.new_value = 'null'
  }

  // 모든 필수 필드가 있는지 확인
  const requiredFields = ['task_id', 'related_id', 'event_type', 'environment', 'author']
  for (const field of requiredFields) {
    if ((sanitized as unknown as Record<string, unknown>)[field] === undefined) {
      console.error(`Missing required field ${field} in event log:`, sanitized)
      // 기본값 설정
      if (field === 'at') (sanitized as unknown as Record<string, unknown>)[field] = getUnixtime()
      else if (field === 'environment')
        (sanitized as unknown as Record<string, unknown>)[field] = isApp() ? 'app' : 'web'
      else if (field === 'author')
        (sanitized as unknown as Record<string, unknown>)[field] = 'unknown'
      else (sanitized as unknown as Record<string, unknown>)[field] = ''
    }
  }

  return sanitized
}

// getLocalData 함수 추가 - 이중 JSON 파싱 적용
const getLocalData = async (taskId: string): Promise<DeviceEventLog[]> => {
  if (!taskId) return []
  const db = await SQL.getDatabase()
  try {
    // 테이블이 없는 경우 빈 배열 반환
    if (!SQL.createdTables.has(storeKey)) {
      throw new Error('테이블이 존재하지 않습니다.')
    }

    // 테이블이 존재하는 경우에만 쿼리 실행
    const localData = await db.query(`SELECT * FROM ${storeKey} WHERE task_id = ?`, [taskId])
    return localData.values?.map((row: DeviceEventLog) => {
      const parsedRow = parseRow(row) as DeviceEventLog

      // JSON 문자열 필드 추가 파싱
      if (parsedRow.new_value) {
        parsedRow.new_value = parseJsonStringValue(parsedRow.new_value) as string
      }
      if (parsedRow.old_value) {
        parsedRow.old_value = parseJsonStringValue(parsedRow.old_value) as string
      }

      return parsedRow
    }) as DeviceEventLog[]
  } catch (error) {
    console.error('Error getting device event log local:', error)
    return []
  }
}

const CLEANUP_LOG_AGE_DAYS = 7

// 7일 초과 synced 로그를 SQLite에서 삭제
const cleanupOldSyncedLogs = async (): Promise<void> => {
  try {
    const db = await SQL.getDatabase()
    if (!SQL.createdTables.has(storeKey)) return
    const cutoff = getUnixtime() - CLEANUP_LOG_AGE_DAYS * 24 * 60 * 60
    const result = await db.run(
      `DELETE FROM ${storeKey} WHERE modifiedFromLocal = 0 AND created_at < ?`,
      [cutoff],
    )
    const deleted = result?.changes?.changes ?? 0
    if (deleted > 0) {
      console.info(
        `[LogStore] Cleaned up ${deleted} old synced logs (>${CLEANUP_LOG_AGE_DAYS} days)`,
      )
    }
  } catch (e) {
    console.warn('[LogStore] Failed to cleanup old logs:', e)
  }
}

// 진단용: 이벤트 로그 누적량과 DB 크기를 콘솔에 기록 (읽기 전용, 동작 변경 없음)
const logEventLogDiagnostics = async (taskId: string): Promise<void> => {
  try {
    const db = await SQL.getDatabase()
    if (!SQL.createdTables.has(storeKey)) return

    const totalRes = await db.query(`SELECT COUNT(*) AS cnt FROM ${storeKey}`, [])
    const unsyncedRes = await db.query(
      `SELECT COUNT(*) AS cnt FROM ${storeKey} WHERE modifiedFromLocal = 1`,
      [],
    )
    const taskRes = await db.query(`SELECT COUNT(*) AS cnt FROM ${storeKey} WHERE task_id = ?`, [
      taskId,
    ])
    const pageCountRes = await db.query('PRAGMA page_count', [])
    const pageSizeRes = await db.query('PRAGMA page_size', [])

    const total = Number(totalRes.values?.[0]?.cnt ?? -1)
    const unsynced = Number(unsyncedRes.values?.[0]?.cnt ?? -1)
    const taskCount = Number(taskRes.values?.[0]?.cnt ?? -1)
    // PRAGMA 결과는 플러그인에 따라 키 이름이 다를 수 있어 첫 번째 값으로 추출
    const pageCount = Number(Object.values(pageCountRes.values?.[0] ?? {})[0] ?? 0)
    const pageSize = Number(Object.values(pageSizeRes.values?.[0] ?? {})[0] ?? 0)
    const dbSizeMb = ((pageCount * pageSize) / (1024 * 1024)).toFixed(1)

    console.info(
      `[LogStore] event_log total=${total}, unsynced=${unsynced}, currentTask=${taskCount}, dbSize=${dbSizeMb}MB`,
    )
  } catch (e) {
    console.warn('[LogStore] Failed to collect diagnostics:', e)
  }
}

// 변경된 필드를 깊게(deep) 추출하는 유틸리티 함수
const extractChangedFields = (
  before: unknown,
  after: unknown,
  parentKey = '',
): { key: string; before: unknown; after: unknown }[] => {
  if (!before) {
    // 새로운 객체인 경우 _modified가 붙지 않은 키만 추출
    return Object.keys(after as Record<string, unknown>)
      .filter(
        key => !key.endsWith('_modified') && key !== 'defect_photos' && key !== 'modifiedFromLocal',
      )
      .map(key => {
        const currentKey = parentKey ? `${parentKey}.${key}` : key
        return {
          key: currentKey,
          before: undefined,
          after: (after as Record<string, unknown>)[key],
        }
      })
  }

  const changedFields: { key: string; before: unknown; after: unknown }[] = []

  // after의 모든 키를 확인 (_modified로 끝나는 키와 modifiedFromLocal, defect_photos는 제외)
  Object.keys(after as Record<string, unknown>)
    .filter(
      key => !key.endsWith('_modified') && key !== 'defect_photos' && key !== 'modifiedFromLocal',
    )
    .forEach(key => {
      const beforeValue = (before as Record<string, unknown>)[key]
      const afterValue = (after as Record<string, unknown>)[key]
      const currentKey = parentKey ? `${parentKey}.${key}` : key

      // 둘 다 객체이고 null이 아닐 경우 재귀적으로 비교
      if (
        typeof beforeValue === 'object' &&
        typeof afterValue === 'object' &&
        beforeValue !== null &&
        afterValue !== null &&
        !Array.isArray(beforeValue) &&
        !Array.isArray(afterValue)
      ) {
        // 재귀적으로 중첩된 객체 비교
        const nestedChanges = extractChangedFields(beforeValue, afterValue, currentKey)
        changedFields.push(...nestedChanges)
      }
      // 배열인 경우는 lodash의 isEqual로 깊은 비교
      else if (Array.isArray(beforeValue) && Array.isArray(afterValue)) {
        if (!_.isEqual(beforeValue, afterValue)) {
          changedFields.push({
            key: currentKey,
            before: beforeValue,
            after: afterValue,
          })
        }
      }
      // 일반 값이 다른 경우
      else if (!_.isEqual(beforeValue, afterValue)) {
        changedFields.push({
          key: currentKey,
          before: beforeValue,
          after: afterValue,
        })
      }
    })

  return changedFields
}

export const findChangedItems = <T extends { id: string | number; localStringId: string }>(
  previousItems: T[],
  currentItems: T[],
): ChangedItem[] => {
  // 결과는 logChanges로만 쓰이므로 기능 비활성화 시 비교(JSON 왕복·깊은 비교) 자체를 생략
  if (!DEVICE_EVENT_LOG_ENABLED) return []
  const prevItemsJson = JSON.parse(JSON.stringify(previousItems)) as T[]
  const currItemsJson = JSON.parse(JSON.stringify(currentItems)) as T[]

  // 변경된 아이템 찾기 - modifiedFromLocal 속성 제외하고 비교
  const changedItems = currItemsJson.filter(currentItem => {
    const currentId = isApp() ? currentItem.localStringId : currentItem.id
    const prevItem = prevItemsJson.find(
      prev => (isApp() ? prev.localStringId : prev.id) == currentId,
    )
    if (!prevItem) return true // 새로 추가된 아이템

    // modifiedFromLocal 속성을 제외한 복사본 생성
    const prevItemWithoutModified = { ...prevItem } as T
    const currentItemWithoutModified = { ...currentItem } as T

    // 수정된 복사본으로 비교
    return !_.isEqual(currentItemWithoutModified, prevItemWithoutModified)
  })

  // 변경 내역 생성
  const changes = changedItems.map(currentItem => {
    const currentId = isApp() ? currentItem.localStringId : currentItem.id
    const prevItem = prevItemsJson.find(
      prev => (isApp() ? prev.localStringId : prev.id) == currentId,
    )
    const changedFields = extractChangedFields(prevItem, currentItem)

    return {
      id: currentId,
      isNew: !prevItem,
      changedFields,
    }
  })
  return changes
}

// 로그 적재 호출 함수
export const logChanges = (storeName: string, changes: ChangedItem[]): void => {
  // 기능 비활성화 시 로그 생성 차단 (호출처는 그대로 유지)
  if (!DEVICE_EVENT_LOG_ENABLED) return
  const logStore = useDeviceEventLogStore()
  if (changes.length > 0) {
    for (const change of changes) {
      const { id, isNew, changedFields } = change
      if (!changedFields.length) return
      const setLogItems = changedFields
        .map(field => {
          const { key, before, after } = field
          const eventType = `${storeName.toLowerCase()}.${key}.${isNew ? 'create' : 'update'}`
          if (EXCLUDE_API_UPDATE_KEYS.find(excludeKey => eventType.includes(excludeKey))) {
            return null
          }
          return {
            type: eventType,
            related_id: `${id}`,
            new_value: after,
            old_value: before,
          }
        })
        .filter(item => item !== null)

      // 모든 스토어별 로그 항목을 즉시 처리하기 위해 별도의 호출 사용
      logStore.addEventLogAll(setLogItems, storeName)
    }
  }
}

export const getLogKey = (storeName: string, isNew: boolean, key: string): string => {
  if (isNew) {
    return `${storeName.toLowerCase()}.${key}.create`
  } else {
    return `${storeName.toLowerCase()}.${key}.update`
  }
}

export const useDeviceEventLogStore = defineStore('app-device-event-log', () => {
  const eventLogs = ref<DeviceEventLog[]>([])
  const currentTaskDatabaseId = ref<string>('')

  const notUploadedLogs = computed(() => {
    return eventLogs.value.filter(item => item.modifiedFromLocal)
  })
  async function uploadNotUploaded(): Promise<void> {
    if (!DEVICE_EVENT_LOG_ENABLED) return
    if (notUploadedLogs.value.length > 0) {
      // 앱 환경에서만 호출 (웹은 즉시 전송)
      if (isApp()) {
        await requestUpdate(notUploadedLogs.value)
      }
    }
  }
  async function individualSetLocalModified(
    localLogId: string,
    keyValues: { key: string; value: unknown }[] = [],
  ): Promise<void> {
    if (isApp()) {
      const db = await SQL.getDatabase()
      let sql = `UPDATE ${storeKey} SET `
      for (let i = 0; i < keyValues.length; i++) {
        const keyValue = keyValues[i]
        sql += `${keyValue.key} = ?`
        if (i < keyValues.length - 1) sql += ', '
      }
      sql += ' WHERE id = ?'
      // 값 정규화: iOS native bridge가 number를 TEXT 컬럼에 "557.0"으로 저장하는 것을 차단
      await db.run(sql, [...keyValues.map(item => prepareSQLiteValue(item.value)), localLogId])
    }
    const eventLogIndex = eventLogs.value.findIndex(item => item.id === localLogId)
    if (eventLogIndex !== -1) {
      const eventLogToUpdate = eventLogs.value[eventLogIndex]
      for (const keyValue of keyValues) {
        ;(eventLogToUpdate as unknown as Record<string, unknown>)[keyValue.key] = keyValue.value
      }
    }
  }

  const checkDeviceEventLogTable = async (): Promise<boolean> => {
    const db = await SQL.getDatabase()
    const result = await db.query(
      `SELECT name FROM sqlite_master WHERE type='table' AND name='${storeKey}'`,
    )
    return result.values?.length > 0
  }

  async function initData(taskDatabaseId: string): Promise<boolean> {
    // 기능 비활성화 시 테이블 생성/정리/로드/큐 적재 전부 스킵
    if (!DEVICE_EVENT_LOG_ENABLED) {
      console.info('[LogStore] device_event_log 기능 비활성화 상태 — initData 스킵')
      return false
    }
    let isMakedTable = false
    const tableData = {
      id: '',
      task_id: 0,
      related_id: 0,
      event_type: '',
      environment: '',
      author: '',
      old_value: '',
      new_value: '',
      at: 0,
      modifiedFromLocal: false,
      localRelatedId: '',
    }
    try {
      const db = await SQL.getDatabase()
      if (!SQL.createdTables.has(storeKey)) {
        const createTableSQL = await getDynamicTableSql(storeKey, tableData)
        if (createTableSQL && createTableSQL.trim()) {
          await db.execute(createTableSQL)
          SQL.createdTables.add(storeKey)
          isMakedTable = true
        }
      } else {
        if (isApp()) {
          // 7일 초과 synced 로그 SQLite에서 정리
          await cleanupOldSyncedLogs()

          // 진단용: 누적량/DB 크기 기록
          await logEventLogDiagnostics(taskDatabaseId)

          const localData = await getLocalData(taskDatabaseId)
          if (localData.length > 0) {
            const unsyncedLogs = localData.filter(item => item.modifiedFromLocal)
            // 메모리에는 미동기화 로그만 로드 (synced 로그는 서버에 있음)
            eventLogs.value = unsyncedLogs
            // 방금 SQLite에서 읽은 데이터를 그대로 재기록할 필요가 없으므로 API 큐 적재만 수행
            await addApiQueue(unsyncedLogs)
          }
        }
      }
      if (isMakedTable) {
        const result = await db.query(
          `SELECT name FROM sqlite_master WHERE type='table' AND name='${storeKey}'`,
        )
        return result.values?.length > 0
      }
    } catch (e) {
      console.error('Error in initData:', e)
      throw e
    }
    return isMakedTable
  }
  let isSyncing = false

  /**
   * [웹 전용] 로그를 서버에 즉시 배치 전송하는 함수.
   */
  const syncDataLocalAndServerForWeb = async (datas: DeviceEventLog[]): Promise<void> => {
    if (isSyncing || datas.length === 0) return
    isSyncing = true
    try {
      const isNetworkConnected = (await Network.getStatus()).connected
      if (!isNetworkConnected) {
        console.warn('[LogStore-Web] Network is offline. Skipping log sync.')
        return
      }
      const logsToServer = datas.map(log => {
        return {
          ...sanitizeEventLogForServer(log),
          related_id: Number(log.related_id),
        }
      })
      await APIServer.addDeviceEventLog(logsToServer)
      console.info(`[LogStore-Web] ${logsToServer.length} logs have been sent to the server.`)
    } catch (e) {
      console.error('Error in syncDataLocalAndServerForWeb:', e)
    } finally {
      isSyncing = false
    }
  }

  const updateSqliteOnly = async (datas: DeviceEventLog[]): Promise<void> => {
    try {
      const db = await SQL.getDatabase()
      const tableExists = await checkDeviceEventLogTable()
      if (!tableExists) {
        const createTableSQL = await getDynamicTableSql(storeKey, datas[0])
        if (createTableSQL) await db.execute(createTableSQL)
        SQL.createdTables.add(storeKey)
      }
      const columnsResult = await db.query(`PRAGMA table_info(${storeKey})`)
      const columns = columnsResult.values.map((col: { name: string }) => col.name)
      const insertColumns = ['id']
      for (const [key] of Object.entries(datas[0])) {
        if (key === 'id') continue
        if (columns.includes(key)) insertColumns.push(key)
      }
      const BATCH_SIZE = 50
      for (let i = 0; i < datas.length; i += BATCH_SIZE) {
        const batch = datas.slice(i, i + BATCH_SIZE)
        const batchValuePlaceholders = []
        const batchValues = []
        for (const item of batch) {
          const valuePlaceholders = ['?']
          batchValues.push(item.id)
          for (let j = 1; j < insertColumns.length; j++) {
            const key = insertColumns[j]
            valuePlaceholders.push('?')
            batchValues.push(prepareSQLiteValue((item as unknown as Record<string, unknown>)[key]))
          }
          batchValuePlaceholders.push(`(${valuePlaceholders.join(', ')})`)
        }
        const sql = `INSERT OR REPLACE INTO ${storeKey} (${insertColumns.join(', ')}) VALUES ${batchValuePlaceholders.join(', ')}`
        await db.run(sql, batchValues)
      }
    } catch (e) {
      console.error('Error in syncDataLocalAndServerForApp:', e)
    }
  }

  // 로그 배치 1개(job 1개)에 담는 최대 로그 수 — 단일 거대 job/API 호출로 인한
  // 페이로드 초과·메모리 폭증·큐 장기 점유 방지
  const LOG_BATCH_CHUNK_SIZE = 500

  const addApiQueue = async (datas: DeviceEventLog[]): Promise<void> => {
    if (!DEVICE_EVENT_LOG_ENABLED) return
    if (datas.length === 0) return
    const needToUpload = datas.filter(item => item.modifiedFromLocal)
    if (needToUpload.length === 0) return
    // 이미 처리 대기 중인 로그 배치가 있으면 같은 로그가 중복 적재되므로 스킵
    // (남은 로그는 다음 주기 sync에서 다시 적재됨)
    if (syncQueue.hasActiveJobByTag('log-batch')) {
      console.info(
        `[LogStore] 로그 배치가 이미 큐에 있어 적재 스킵 (대상 ${needToUpload.length}건)`,
      )
      return
    }
    // 1회 적재당 청크 수 상한 — 누적 로그가 수만 건이어도 큐를 점령하지 않도록 제한.
    // 잔여분은 다음 주기 sync의 적재에서 이어서 처리됨.
    const MAX_CHUNKS_PER_ROUND = 20
    const uploadTarget = needToUpload.slice(0, LOG_BATCH_CHUNK_SIZE * MAX_CHUNKS_PER_ROUND)
    if (uploadTarget.length < needToUpload.length) {
      console.info(
        `[LogStore] 로그 ${needToUpload.length}건 중 ${uploadTarget.length}건만 이번 회차에 적재 (잔여분은 다음 sync에서 처리)`,
      )
    }
    for (let i = 0; i < uploadTarget.length; i += LOG_BATCH_CHUNK_SIZE) {
      addLogBatchJobToQueue(uploadTarget.slice(i, i + LOG_BATCH_CHUNK_SIZE))
    }
  }

  const requestUpdate = async (datas: DeviceEventLog[]): Promise<void> => {
    if (!isApp()) {
      await syncDataLocalAndServerForWeb(datas)
      return
    }
    await updateSqliteOnly(datas)
    await addApiQueue(datas)
  }

  /**
   * 여러 DeviceEventLog를 하나의 "배치 작업"으로 묶어 SyncQueue에 추가하는 헬퍼 함수
   * @param logBatch 동기화할 DeviceEventLog 데이터 배열
   */
  function addLogBatchJobToQueue(logBatch: DeviceEventLog[]): void {
    if (logBatch.length === 0) return
    // 배치 작업의 execute 콜백
    const executeCallback = async (
      batchData: DeviceEventLog[],
    ): Promise<{ processedLogs: DeviceEventLog[] }> => {
      const logsToSend: DeviceEventLog[] = []

      // 배치 내 모든 localRelatedId를 스토어 타입별로 수집
      const defectIds: string[] = []
      const photoIds: string[] = []
      for (const log of batchData) {
        const localRelatedId = log.localRelatedId ?? String(log.related_id)
        const storeType = getEventType(log.event_type)
        if (storeType === 'defects') defectIds.push(localRelatedId)
        else if (storeType === 'defect_photos') photoIds.push(localRelatedId)
      }

      // 벌크 쿼리로 한 번에 조회 (개별 쿼리 대신)
      const [defectMap, photoMap] = await Promise.all([
        defectIds.length > 0 ? syncQueue.findIdsBatch(defectIds, 'defects') : new Map(),
        photoIds.length > 0 ? syncQueue.findIdsBatch(photoIds, 'defect_photos') : new Map(),
      ])

      // 배치 내의 모든 로그에 대해 의존성 검사
      for (const log of batchData) {
        const localRelatedId = log.localRelatedId ?? String(log.related_id)
        const storeType = getEventType(log.event_type)

        let serverRelatedId: number | string = -1
        if (localRelatedId === 'branch_image') {
          serverRelatedId = -1
        } else if (storeType === 'defects') {
          serverRelatedId = defectMap.get(localRelatedId) ?? '_need_to_upload'
        } else if (storeType === 'defect_photos') {
          serverRelatedId = photoMap.get(localRelatedId) ?? '_need_to_upload'
        }

        if (serverRelatedId === 'is_deleted') {
          await individualSetLocalModified(String(log.id), [
            { key: 'deleted', value: Merge.getCurrentTime() },
            { key: 'modifiedFromLocal', value: false },
          ])
          continue
        }
        if (String(serverRelatedId).includes('_need_to_upload') || isNaN(Number(serverRelatedId))) {
          throw new DependencyNotReadyError(
            `Dependent item ${localRelatedId} for log ${log.id} is not synced yet. Batch will be retried.`,
          )
        }
        // 의존성 해결된 로그를 서버에 보낼 목록에 추가
        await individualSetLocalModified(String(log.id), [
          { key: 'related_id', value: serverRelatedId },
        ])
        logsToSend.push({
          ..._.pick(log, ADD_LOG_KEYS),
          related_id: Number(serverRelatedId),
        } as DeviceEventLog)
      }
      // 준비된 모든 로그를 단일 API 호출로 전송
      if (logsToSend.length > 0) {
        const sanitizedLogs = logsToSend.map(log => sanitizeEventLogForServer(log))
        await APIServer.addDeviceEventLog(sanitizedLogs)
      }

      // 성공 시, 처리된 로그 목록을 그대로 반환하여 onSuccess에서 사용
      return { processedLogs: batchData }
    }

    syncQueue.addJob({
      // 배치 작업은 특정 localStringId가 없으므로, 고유한 배치 ID를 생성
      localStringId: `log-batch-${getUnixtime()}-${getRandomString(4, false)}`,
      data: logBatch, // 데이터는 로그 배열 전체
      priority: JobPriority.VERY_LOW, // 로그 작업은 우선순위 매우 낮게 설정
      maxRetries: 3, // 배치 작업은 재시도 횟수를 낮게 설정
      tag: 'log-batch',
      execute: executeCallback,
      onSuccess: async (_, data) => {
        // 성공한 모든 로그에 대해 DB 상태 업데이트
        if (isApp()) {
          // 다중 로그 ID를 한 번에 처리하기 위한 준비
          const logIds = data.filter(log => log.modifiedFromLocal).map(log => String(log.id))

          if (logIds.length > 0) {
            try {
              // 단일 SQL 쿼리로 여러 로그의 modifiedFromLocal 상태를 false로 업데이트
              const db = await SQL.getDatabase()
              const placeholders = logIds.map(() => '?').join(',')
              const sql = `UPDATE ${storeKey} SET modifiedFromLocal = 0 WHERE id IN (${placeholders})`
              await db.run(sql, logIds)

              // 동기화 완료된 로그를 메모리에서 제거 (무한 성장 방지)
              const syncedIdSet = new Set(logIds)
              eventLogs.value = eventLogs.value.filter(item => !syncedIdSet.has(String(item.id)))
            } catch (error) {
              console.error('[SyncQueue] Failed to update log sync states:', error)
            }
          }
        }
      },

      onError: (error, data) => {
        console.error(`[SyncQueue] Log BATCH job with ${data.length} logs FAILED.`, error)
      },

      onIdMap: undefined,
    })
  }

  // 대기 중인 이벤트 로그 관리
  const pendingLogs = ref<DeviceEventLog[]>([])

  // 배치 처리 로직을 적용한 새 함수
  const processAllPendingLogs = async (): Promise<void> => {
    if (pendingLogs.value.length === 0) return

    // 이제 logs 속성을 참조할 필요가 없습니다.
    const logsToProcess = [...pendingLogs.value]
    pendingLogs.value = [] // 먼저 비워서 중복 처리 방지

    eventLogs.value.push(...logsToProcess)
    await requestUpdate(logsToProcess)
  }

  // 배치 처리 호출 (디바운스 적용)
  const triggerProcessPendingLogs = _.debounce(processAllPendingLogs, 500)

  // 수정된 addEventLogAll 함수
  const addEventLogAll = async (
    items: LogItem[],
    _storeName: string = 'unknown',
  ): Promise<void> => {
    if (!DEVICE_EVENT_LOG_ENABLED) return
    const addedTime = Merge.getCurrentTime()
    const taskId = App.Router.currentRoute.value.params.taskId as string
    if (!taskId || !items.length) return
    const currentId = await getDeviceOrClientId()

    const eventLogsAdd = items.map(item => {
      const { type, related_id, new_value, old_value } = item
      const uniqueId = getRandomString(16, false)
      return {
        id: `${uniqueId}_need_to_upload`,
        task_id: Number(taskId),
        related_id: related_id,
        event_type: type,
        environment: isApp() ? 'app' : 'web',
        author: currentId,
        old_value: JSON.stringify(old_value),
        new_value: JSON.stringify(new_value),
        created_at: addedTime,
        modifiedFromLocal: isApp() ? true : false,
        localRelatedId: isApp() ? String(related_id) : undefined,
      } as DeviceEventLog
    })

    // 이제 전개 연산자(...)를 사용하여 배열을 합칩니다.
    pendingLogs.value.push(...eventLogsAdd)
    triggerProcessPendingLogs()
  }

  const getEventLog = async (taskId: number): Promise<ServerEventLog[]> => {
    try {
      const data = await APIServer.getDeviceEventLog(taskId.toString())
      // 불가피하게 unknown으로 먼저 변환한 후 원하는 타입으로 변환
      const logs = data[0] as unknown as ServerEventLog[]
      return logs
    } catch (e) {
      console.error('Error in getEventLog:', e)
      throw e
    }
  }

  // 이벤트 로그를 CSV 형식으로 변환하는 함수
  const convertLogsToCSV = (logs: ServerEventLog[]): string => {
    if (!logs || logs.length === 0) return ''

    // CSV 헤더 생성
    const headers = [
      'ID',
      '작업 ID',
      '관련 ID',
      '이벤트 유형',
      '환경',
      '작성자',
      '이전 값',
      '새 값',
      '시각',
      '회사',
    ]

    const rows = logs.map(log => {
      const date = new Date(log.created_at)
      const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`

      const oldValue = log.old_value === 'null' ? '' : log.old_value
      const newValue = log.new_value === 'null' ? '' : log.new_value

      return [
        log.id,
        log.task_id,
        log.related_id,
        log.event_type,
        log.environment,
        log.author,
        oldValue,
        newValue,
        formattedDate,
        log.company || '',
      ]
        .map(value => {
          const stringValue = String(value).replace(/"/g, '""')
          return `"${stringValue}"`
        })
        .join(',')
    })

    return [headers.join(','), ...rows].join('\n')
  }

  const downloadLogsAsExcel = async (taskId: number): Promise<void> => {
    try {
      const logs = await getEventLog(taskId)
      if (!logs || logs.length === 0) {
        console.error('다운로드할 로그 데이터가 없습니다.')
        return
      }

      const csvContent = convertLogsToCSV(logs)

      if (!isApp()) {
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')

        const today = new Date()
        const fileName = `이벤트로그_작업${taskId}_${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}.csv`

        link.setAttribute('href', url)
        link.setAttribute('download', fileName)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        console.error('앱 환경에서는 아직 엑셀 다운로드를 지원하지 않습니다.')
      }
    } catch (e) {
      console.error('엑셀 다운로드 중 오류 발생:', e)
      throw e
    }
  }

  const getEventType = (eventType: string): 'defects' | 'defect_photos' | 'unknown' => {
    if (eventType.startsWith('shape')) return 'defects'
    if (eventType.startsWith('defect')) return 'defect_photos'
    return 'unknown'
  }

  async function getAllModifiedData(): Promise<DeviceEventLog[]> {
    const db = await SQL.getDatabase()
    const localData = await db.query(`SELECT * FROM ${storeKey} WHERE modifiedFromLocal = 1`)
    return localData.values?.map((row: DeviceEventLog) => parseRow(row)) as DeviceEventLog[]
  }

  async function deleteLogs(relatedId: string, deletedTime: string): Promise<void> {
    const findCurrentLogs = eventLogs.value.find(log => log.localRelatedId == relatedId)
    if (!findCurrentLogs) return
    await SQL.getDatabase().then(db => {
      return db.query(
        `UPDATE ${storeKey}
         SET deleted = ?, modifiedFromLocal = ?
         WHERE localRelatedId = ?`,
        [deletedTime.toString(), 0, relatedId], // false → 0
      )
    })

    const logs = eventLogs.value.filter(log => log.localRelatedId != relatedId)
    if (logs.length === 0) return
    eventLogs.value = _.cloneDeep(logs)
  }
  return {
    eventLogs,
    currentTaskDatabaseId,
    notUploadedLogs,
    uploadNotUploaded,
    individualSetLocalModified,
    checkDeviceEventLogTable,
    initData,
    getEventLog,
    addEventLogAll,
    pendingLogs,
    processAllPendingLogs,
    downloadLogsAsExcel,
    convertLogsToCSV,
    getAllModifiedData,
    requestUpdate,
    addApiQueue,
    deleteLogs,
  }
})
